
import useActionTranslate from 'FlowBuilder/hooks/useActionTranslate'
import useFlowTranslate from 'FlowBuilder/hooks/useFlowTranslate'
import useLanguage, { useDefaultFlowLanguage } from 'FlowBuilder/hooks/useLanguageFlow'
import { useAct } from 'FlowBuilder/store'
import InlineCkEditor from 'Shared/components/CKEditor/InlineCkEditor'
import FileUploader from 'Shared/components/ImageUploader/FileUploader'
import LiquidValidator from 'Shared/components/LiquidValidator/LiquidValidator'

export default function EditContent() {
  const act = useAct()
  const fieldKind = useSel(s => s.local.modal.fieldKind)
  const actionId = useSel(s => s.local.modal.actionId)
  const slug = useSel(s => s.local.modal.slug)
  const wysiwyg = useSel(s => s.local.modal.wysiwyg)
  const builder = useSel(s => s.builder)
  const language = useLanguage()

  const hook = useSel(s => {
    if (fieldKind === 'actionTextContent') {
      return actionTextContentHooks(act, slug, actionId, wysiwyg)
    } else if (fieldKind === 'templateFlowField') {
      return templateFieldTextContentHooks(builder, act, slug, null)
    } else if (fieldKind === 'templateActionField') {
      return templateFieldTextContentHooks(builder, act, slug, actionId)
    }
  })

  const mergeTags = useSel(s => s.builder.mergeTags)
  const [sourceMode, setSourceMode] = useState(false)

  function onChange(value) {
    hook.updateValue(language, value)
  }

  function handleFileUploaded(blobIds){
    hook.updateValue(language, blobIds[0])
    act.local.closeModal()
  }

  return <div>
    <div className='row'>
      <div className='column remaining'>
        <p className='no-margin-top dark-grey'>{hook.field.label}</p>
        {hook.field.wysiwyg && <>
          <InlineCkEditor
            value={hook.value}
            placeholder={hook.fallbackValue}
            sourceMode={sourceMode}
            setSourceMode={setSourceMode}
            onUpdate={onChange}
            mergeTags={mergeTags}
          />
          <LiquidValidator value={hook.value} lineSplitTags={true} availableTags={mergeTags} />
        </>}
        {!hook.field.wysiwyg && !hook.field.image && <div className='form-group'>
          <input type='text' className='full-width' value={hook.value} placeholder={hook.fallbackValue} onChange={e => onChange(e.target.value)} />
        </div>}
        {hook.field.image && <div className="form-group">
          <FileUploader
            onUploaded={handleFileUploaded}
          />
        </div>}
      </div>
    </div>
  </div>
}

function actionTextContentHooks(act, slug, actionId, wysiwyg) {
  const {
    translated: { [slug]: translatedValue },
    fallback: { [slug]: fallbackValue }
  } = useActionTranslate({ actionId })

  return {
    field: { label: '', wysiwyg: wysiwyg ?? true, translatable: true },
    value: translatedValue || '',
    fallbackValue: fallbackValue || '',
    updateValue: (language, value) => act.actions.updateActionTextContent({ actionId, language, key: slug, value })
  }
}

function templateFieldTextContentHooks(builder, act, slug, actionId = null) {
  const field = builder.flowTemplate.fields.find(f => f.slug === slug)

  if (field.translatable) {
    const defaultLanguage = useDefaultFlowLanguage()

    const {
      translated: { [_.camelCase(slug)]: translatedFlowValue },
      fallback: { [_.camelCase(slug)]: fallbackFlowValue },
    } = useFlowTranslate()

    const {
      translated: { [_.camelCase(slug)]: translatedActionValue },
      fallback: { [_.camelCase(slug)]: fallbackActionValue },
    } = (actionId ? useActionTranslate({ actionId }) : { translated: {}, fallback: {} })

    const updateValue = (language, value) => {
      if (actionId) {
        act.actions.updateActionTextContent({ actionId, language, key: _.camelCase(slug), value })
      } else {
        act.builder.updateFlowTextContent(language, _.camelCase(slug), value)
      }
      if (language == defaultLanguage) {
        act.builder.setTemplateField({ slug, value })
      }
    }

    if (actionId) {
      return {
        field: field,
        value: translatedActionValue || translatedFlowValue || '',
        fallbackValue: fallbackActionValue || fallbackFlowValue || builder.flow.templateFields[slug] || '',
        updateValue
      }
    }

    return {
      field: field,
      value: translatedFlowValue || '',
      fallbackValue: fallbackFlowValue || builder.flow.templateFields[slug] || '',
      updateValue
    }
  }

  return {
    field: field,
    value: builder.flow.templateFields[slug],
    fallbackValue: null,
    updateValue: (language, value) => act.builder.setTemplateField({ slug, value })
  }
}
